import type {
  MembershipAddMemberOption,
  MembershipAddMemberPatient,
  MembershipBenefitClaim,
  MembershipBenefitUsageItem,
  MembershipMemberDetail,
  MembershipPageMember,
  MembershipViewSource,
  MembershipPaymentCycle,
  MembershipPaymentHistoryRow,
  MembershipReactivationHistoryRow,
  MembershipShieldVariant,
  MembershipStatus,
} from '@dataTypes/membership.type'

export const MEMBERSHIP_PAGE_SIZE = 10

export const MEMBERSHIP_PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

export const MEMBERSHIP_TIER_LABELS: Record<MembershipShieldVariant, string> = {
  basic: 'Basic',
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
}

export const membershipTierLabel = (
  member: Pick<MembershipPageMember, 'tier' | 'tierName'>,
): string => member.tierName?.trim() || MEMBERSHIP_TIER_LABELS[member.tier]

export const MEMBERSHIP_TIER_MONTHLY_PRICE: Record<
  MembershipShieldVariant,
  number
> = {
  basic: 29,
  bronze: 39,
  silver: 49,
  gold: 59,
  platinum: 79,
}

export const MEMBERSHIP_STATUS_BADGE_COLOR: Record<
  MembershipStatus,
  'vivid-green' | 'gray' | 'red'
> = {
  Active: 'vivid-green',
  'On hold': 'gray',
  Inactive: 'red',
}

export type MembershipKpiItem = {
  id: string
  label: string
  value: string
  unitInline?: string
  footer: string
  /** Dark-mode footer when the Figma frames disagree. */
  footerDark?: string
  trend: 'positive' | 'negative'
  /** Light-mode arrow direction. Defaults from `trend`. */
  trendDirection?: 'up' | 'down'
  /** Dark-mode arrow direction when it differs from light mode. */
  trendDirectionDark?: 'up' | 'down'
}

export const MEMBERSHIP_PAGE_MEMBERS: MembershipPageMember[] = [
  {
    id: '1',
    name: 'Alex Smith',
    tier: 'basic',
    memberSince: '03/15/2025',
    expiryDate: '07/15/2027',
    paymentCycle: 'Monthly',
    status: 'Active',
  },
  {
    id: '2',
    name: 'Liam Smith',
    tier: 'bronze',
    memberSince: '11/22/2024',
    expiryDate: '11/22/2028',
    paymentCycle: 'Annual',
    status: 'Active',
  },
  {
    id: '3',
    name: 'Olivia Brown',
    tier: 'silver',
    memberSince: '07/04/2026',
    expiryDate: '02/05/2029',
    paymentCycle: 'Monthly',
    status: 'Active',
  },
  {
    id: '4',
    name: 'Noah Davis',
    tier: 'gold',
    memberSince: '09/30/2025',
    expiryDate: '09/30/2027',
    paymentCycle: 'Monthly',
    status: 'Active',
  },
  {
    id: '5',
    name: 'James Taylor',
    tier: 'silver',
    memberSince: '12/08/2024',
    expiryDate: '12/25/2027',
    paymentCycle: 'Annual',
    status: 'On hold',
  },
  {
    id: '6',
    name: 'Isabella Anderson',
    tier: 'bronze',
    memberSince: '06/20/2025',
    expiryDate: '01/14/2029',
    paymentCycle: 'Monthly',
    status: 'Inactive',
  },
  {
    id: '7',
    name: 'Benjamin Thomas',
    tier: 'bronze',
    memberSince: '04/02/2026',
    expiryDate: '08/07/2028',
    paymentCycle: 'Monthly',
    status: 'Active',
    disableEdit: true,
  },
  {
    id: '8',
    name: 'Mia Jackson',
    tier: 'bronze',
    memberSince: '10/10/2024',
    expiryDate: '10/31/2027',
    paymentCycle: 'Annual',
    status: 'On hold',
  },
  {
    id: '9',
    name: 'Lucas White',
    tier: 'bronze',
    memberSince: '08/25/2025',
    expiryDate: '05/21/2029',
    paymentCycle: 'Monthly',
    status: 'Inactive',
  },
  {
    id: '10',
    name: 'Charlotte Harris',
    tier: 'bronze',
    memberSince: '02/14/2026',
    expiryDate: '03/03/2028',
    paymentCycle: 'Monthly',
    status: 'Active',
    disableEdit: true,
  },
  {
    id: '11',
    name: 'Henry Martin',
    tier: 'silver',
    memberSince: '11/03/2025',
    expiryDate: '07/29/2029',
    paymentCycle: 'Annual',
    status: 'On hold',
  },
  {
    id: '12',
    name: 'Amelia Thompson',
    tier: 'gold',
    memberSince: '07/19/2024',
    expiryDate: '09/11/2028',
    paymentCycle: 'Monthly',
    status: 'Inactive',
  },
  {
    id: '13',
    name: 'Alexander Garcia',
    tier: 'bronze',
    memberSince: '05/06/2025',
    expiryDate: '11/06/2027',
    paymentCycle: 'Monthly',
    status: 'Active',
    disableEdit: true,
  },
  {
    id: '14',
    name: 'Evelyn Martinez',
    tier: 'silver',
    memberSince: '01/29/2026',
    expiryDate: '02/25/2029',
    paymentCycle: 'Annual',
    status: 'Active',
  },
  {
    id: '15',
    name: 'Daniel Rodriguez',
    tier: 'silver',
    memberSince: '12/22/2025',
    expiryDate: '08/19/2027',
    paymentCycle: 'Annual',
    status: 'On hold',
  },
  {
    id: '16',
    name: 'Ella Lewis',
    tier: 'gold',
    memberSince: '06/11/2024',
    expiryDate: '10/02/2028',
    paymentCycle: 'Monthly',
    status: 'Inactive',
  },
  {
    id: '17',
    name: 'Ava Miller',
    tier: 'basic',
    memberSince: '08/30/2026',
    expiryDate: '12/14/2029',
    paymentCycle: 'Annual',
    status: 'Inactive',
  },
]

export const MEMBERSHIP_DETAIL_PAGE_SIZE = 10

export const MEMBERSHIP_DETAIL_PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

export const MEMBERSHIP_BENEFITS_PAGE_SIZE = 10

export const MEMBERSHIP_BENEFITS_PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

export const MEMBERSHIP_CYCLE_AMOUNT: Record<MembershipPaymentCycle, number> = {
  Monthly: 59,
  Annual: 590,
}

const MEMBERSHIP_LONG_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
})

const formatMembershipLongDate = (value: string): string => {
  const [month, day, year] = value.split('/').map(Number)
  if (!month || !day || !year) return value
  return MEMBERSHIP_LONG_DATE_FORMATTER.format(new Date(year, month - 1, day))
}

const nextMembershipPaymentDate = (
  memberSince: string,
  paymentCycle: MembershipPaymentCycle,
): string => {
  const [month, day, year] = memberSince.split('/').map(Number)
  if (!month || !day || !year) return memberSince

  const step = paymentCycle === 'Annual' ? 12 : 1
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const cursor = new Date(year, month - 1, day)
  while (cursor < today) {
    cursor.setMonth(cursor.getMonth() + step)
  }

  return MEMBERSHIP_LONG_DATE_FORMATTER.format(cursor)
}

const formatMembershipAmount = (amount: number): string =>
  Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`

export const getMembershipOverviewForMember = (
  member: MembershipPageMember,
): {
  tier: string
  memberSince: string
  billingFrequency: string
  nextPaymentDate: string
} => {
  const amount =
    member.recurringAmount ?? MEMBERSHIP_TIER_MONTHLY_PRICE[member.tier]
  const period = member.paymentCycle === 'Annual' ? 'yr' : 'mo'
  return {
    tier: `${membershipTierLabel(member)} - ${formatMembershipAmount(amount)}/${period}`,
    memberSince: formatMembershipLongDate(member.memberSince),
    billingFrequency: member.paymentCycle,
    nextPaymentDate: `Due on ${nextMembershipPaymentDate(
      member.memberSince,
      member.paymentCycle,
    )}`,
  }
}

export const MEMBERSHIP_BENEFIT_USAGE: MembershipBenefitUsageItem[] = [
  { id: 'cleanings', label: 'Cleanings', used: 1, total: 2 },
  { id: 'exams', label: 'Exams', used: 1, total: 2 },
  { id: 'xrays', label: 'X-rays', used: 1, total: 1 },
  { id: 'emergency', label: 'Emergency', used: 0, total: 1 },
]

export const MEMBERSHIP_BENEFIT_CLAIMS: MembershipBenefitClaim[] = [
  { id: 'claim-1', date: '2026-08-06', procedure: 'X-Ray', amount: '$75' },
  { id: 'claim-2', date: '2026-08-06', procedure: 'Exam', amount: '$75' },
  { id: 'claim-3', date: '2026-08-06', procedure: 'Exam', amount: '$75' },
  { id: 'claim-4', date: '2026-08-06', procedure: 'Exam', amount: '$75' },
  { id: 'claim-5', date: '2026-08-06', procedure: 'Cleaning', amount: '$75' },
]

export const MEMBERSHIP_PAYMENT_HISTORY: MembershipPaymentHistoryRow[] = [
  {
    id: 'pay-2014',
    paymentDate: 'Nov 11, 2014',
    paymentMethod: 'Debit Card',
    paymentType: 'Manual',
    paymentStatus: 'Confirmed',
  },
  {
    id: 'pay-2015',
    paymentDate: 'Nov 11, 2015',
    paymentMethod: 'Debit Card',
    paymentType: 'Auto Payment',
    paymentStatus: 'Confirmed',
  },
  {
    id: 'pay-2016',
    paymentDate: 'Nov 11, 2016',
    paymentMethod: 'Debit Card',
    paymentType: 'Manual',
    paymentStatus: 'Confirmed',
  },
  {
    id: 'pay-2017',
    paymentDate: 'Nov 11, 2017',
    paymentMethod: 'Debit Card',
    paymentType: 'Auto Payment',
    paymentStatus: 'Confirmed',
  },
  {
    id: 'pay-2018',
    paymentDate: 'Nov 11, 2018',
    paymentMethod: 'Debit Card',
    paymentType: 'Manual',
    paymentStatus: 'Confirmed',
  },
  {
    id: 'pay-2019',
    paymentDate: 'Nov 11, 2019',
    paymentMethod: 'Debit Card',
    paymentType: 'Auto Payment',
    paymentStatus: 'Confirmed',
  },
  {
    id: 'pay-2020',
    paymentDate: 'Nov 11, 2020',
    paymentMethod: 'Debit Card',
    paymentType: 'Manual',
    paymentStatus: 'Confirmed',
  },
  {
    id: 'pay-2021',
    paymentDate: 'Nov 11, 2021',
    paymentMethod: 'Debit Card',
    paymentType: 'Auto Payment',
    paymentStatus: 'Confirmed',
  },
  {
    id: 'pay-2022',
    paymentDate: 'Nov 11, 2022',
    paymentMethod: 'Debit Card',
    paymentType: 'Manual',
    paymentStatus: 'Confirmed',
  },
  {
    id: 'pay-2023',
    paymentDate: 'Nov 11, 2023',
    paymentMethod: 'Credit Card',
    paymentType: 'Auto Payment',
    paymentStatus: 'Confirmed',
  },
]

export const MEMBERSHIP_REACTIVATION_HISTORY: MembershipReactivationHistoryRow[] =
  [
    {
      id: 'react-2014',
      date: 'Nov 11, 2014',
      type: 'Cancellation',
      reason: 'Not seeing enough value',
    },
    {
      id: 'react-2015',
      date: 'Nov 11, 2015',
      type: 'Reactivation',
      reason: '-',
    },
    {
      id: 'react-2016',
      date: 'Nov 11, 2016',
      type: 'Renewal',
      reason: '-',
    },
    {
      id: 'react-2017',
      date: 'Nov 11, 2017',
      type: 'Renewal',
      reason: '-',
    },
    {
      id: 'react-2018',
      date: 'Nov 11, 2018',
      type: 'On-hold',
      reason: 'Moving or relocating',
    },
    {
      id: 'react-2019',
      date: 'Nov 11, 2019',
      type: 'Reactivation',
      reason: '-',
    },
    {
      id: 'react-2020',
      date: 'Nov 11, 2020',
      type: 'Renewal',
      reason: '-',
    },
    {
      id: 'react-2021',
      date: 'Nov 11, 2021',
      type: 'On-hold',
      reason: 'Treatment cost concerns',
    },
    {
      id: 'react-2022',
      date: 'Nov 11, 2022',
      type: 'Reactivate',
      reason: '-',
    },
    {
      id: 'react-2023',
      date: 'Nov 11, 2023',
      type: 'Renewal',
      reason: '-',
    },
  ]

const membershipMemberNumber = (memberId: string): string => {
  const numericId = Number(memberId)
  if (Number.isFinite(numericId)) {
    return `MEM${3884001 + numericId}`
  }

  const compactId = memberId.replace(/[^a-zA-Z0-9]/g, '').slice(0, 7)
  return `MEM${compactId.toUpperCase()}`
}

export const buildMembershipMemberDetail = (
  member: MembershipViewSource,
): MembershipMemberDetail => {
  const billingAmount =
    member.recurringAmount ?? MEMBERSHIP_CYCLE_AMOUNT[member.paymentCycle]

  return {
    ...member,
    role: 'Patient',
    memberNumber: membershipMemberNumber(member.id),
    memberSinceLabel: formatMembershipLongDate(member.memberSince),
    nextPaymentDate: nextMembershipPaymentDate(
      member.memberSince,
      member.paymentCycle,
    ),
    billingLabel: `${formatMembershipAmount(billingAmount)} (${member.paymentCycle})`,
    autoPayMethod: member.autoPayMethod ?? 'Debit card - visa ending in 4553',
    notificationChannels:
      member.notificationChannels ??
      (member.membershipTierId ? '-' : 'Email & SMS'),
    benefitUsage:
      member.benefitUsage ??
      (member.membershipTierId ? [] : MEMBERSHIP_BENEFIT_USAGE),
    totalAvailedDiscounts:
      member.totalAvailedDiscounts ?? (member.membershipTierId ? '$0' : '$375'),
    totalAvailedDiscountsPercent:
      member.totalAvailedDiscountsPercent ?? (member.membershipTierId ? 0 : 50),
    benefitClaims:
      member.benefitClaims ??
      (member.membershipTierId ? [] : MEMBERSHIP_BENEFIT_CLAIMS),
    paymentHistory:
      member.paymentHistory ??
      (member.membershipTierId ? [] : MEMBERSHIP_PAYMENT_HISTORY),
    reactivationHistory:
      member.reactivationHistory ??
      (member.membershipTierId ? [] : MEMBERSHIP_REACTIVATION_HISTORY),
  }
}

export const getMembershipMemberDetail = (
  memberId: string,
): MembershipMemberDetail | undefined => {
  const member = MEMBERSHIP_PAGE_MEMBERS.find((row) => row.id === memberId)
  if (!member) return undefined

  return buildMembershipMemberDetail(member)
}

export const ADD_MEMBER_STEPS = [
  'Select patient',
  'Plan & billing',
  'Notify patient',
  'Review',
]

export const ADD_MEMBER_PATIENTS: MembershipAddMemberPatient[] = [
  {
    id: 'patient-alex-1',
    name: 'Alex Smith',
    kind: 'patient',
    avatarColor: 'red',
    phone: '+1 334 3445',
    email: 'alex@rmail.com',
  },
  {
    id: 'patient-jane',
    name: 'Jane Rowes',
    kind: 'patient',
    avatarColor: 'orange',
    phone: '(201) 555-0144',
    email: 'jane.rowes@rmail.com',
  },
  {
    id: 'patient-fred',
    name: 'Fred Jones',
    kind: 'patient',
    avatarColor: 'gray',
    phone: '(201) 555-0188',
    email: 'fred.jones@rmail.com',
  },
  {
    id: 'patient-alex-2',
    name: 'Alex Smith',
    kind: 'patient',
    avatarColor: 'pink',
    phone: '(201) 555-0199',
    email: 'alex.smith@rmail.com',
  },
  {
    id: 'patient-phone',
    name: '(201) 555-0123',
    kind: 'phone',
    avatarColor: 'avatar',
    phone: '(201) 555-0123',
  },
]

export const MEMBERSHIP_BILLING_FREQUENCIES: MembershipAddMemberOption[] = [
  {
    value: 'monthly',
    title: 'Monthly',
    description: 'Every 30 days from payment date',
  },
  {
    value: 'annual',
    title: 'Annual',
    description: 'Every 12 months from payment date',
  },
]

export const ADD_MEMBER_FREQUENCIES: MembershipAddMemberOption[] =
  MEMBERSHIP_BILLING_FREQUENCIES.filter(
    (option) => option.value !== 'quarterly',
  )

export const ADD_MEMBER_AUTO_PAY_METHODS: MembershipAddMemberOption[] = [
  {
    value: 'debit',
    title: 'Debit card',
    descriptionPrefix: 'Visa ending in ',
    lastFour: '4553',
  },
  {
    value: 'credit',
    title: 'Credit card',
    descriptionPrefix: 'Visa ending in ',
    lastFour: '3342',
  },
  {
    value: 'digital-bank',
    title: 'Digital bank FT',
    descriptionPrefix: 'Bank Accnt. ending in ',
    lastFour: '3342',
  },
]

export const MANAGE_RECURRING_AUTO_PAY_METHODS: MembershipAddMemberOption[] = [
  ADD_MEMBER_AUTO_PAY_METHODS[0],
  ADD_MEMBER_AUTO_PAY_METHODS[1],
  {
    value: 'credit-2',
    title: 'Credit card',
    descriptionPrefix: 'Visa ending in ',
    lastFour: '3342',
  },
]

export const MANAGE_PAYMENT_COUNTRIES = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'AU', label: 'Australia' },
]

export const ADD_MEMBER_PLAN_INCLUSIONS = [
  '2 Free cleaning/yr',
  '2 Free exam/yr',
  '5% off all procedures',
  'Free Bitewing & panoramic X-rays',
  'Free Fluoride Treatment',
]

export const ADD_MEMBER_NOTIFY_OPTIONS: MembershipAddMemberOption[] = [
  {
    value: 'email',
    title: 'Email only',
    description: 'Only send email to member',
  },
  {
    value: 'sms',
    title: 'SMS only',
    description: 'Only send SMS to member',
  },
  {
    value: 'email-sms',
    title: 'Email & SMS',
    description: 'Notify thru email & SMS',
  },
]

export const wantsMembershipWelcomeEmail = (notify: string): boolean =>
  notify === 'email' || notify === 'email-sms'

const escapeMembershipEmailHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

export const applyMembershipOnboardingPlaceholders = (
  template: string,
  patientName: string,
): string =>
  template.replace(/\[patient name\]/gi, escapeMembershipEmailHtml(patientName))

export const buildMembershipWelcomeEmailBody = (
  patientName: string,
  planName: string,
  inclusions: string[],
): string => {
  const items = inclusions
    .map((item) => `<li>${escapeMembershipEmailHtml(item)}</li>`)
    .join('')

  return [
    `<p>Hello ${escapeMembershipEmailHtml(patientName)},</p>`,
    `<p>Welcome to your ${escapeMembershipEmailHtml(planName)} membership! We are excited to have you as part of our dental family.</p>`,
    '<p>Inclusions:</p>',
    `<ul>${items}</ul>`,
  ].join('')
}

export const membershipNotifyEmailBody = (
  patientName: string,
  planName: string,
  inclusions: string[],
  template?: string,
): string =>
  template?.trim()
    ? applyMembershipOnboardingPlaceholders(template, patientName)
    : buildMembershipWelcomeEmailBody(patientName, planName, inclusions)

export const ADD_MEMBER_DEFAULTS = {
  frequency: 'monthly',
  autoPay: 'debit',
  planId: 'gold',
  notify: 'email-sms',
} as const

export const UPDATE_PLAN_DEFAULTS = {
  frequency: 'monthly',
  autoPay: 'debit',
  planId: 'bronze',
  notify: 'email-sms',
} as const

export const CANCEL_MEMBERSHIP_REASONS = [
  { value: 'cost-pressure', label: 'Cost / financial pressure' },
  { value: 'not-enough-value', label: 'Not seeing enough value' },
  { value: 'moving', label: 'Moving or relocating' },
  { value: 'dental-insurance', label: 'Obtained dental insurance' },
  { value: 'changed-provider', label: 'Changed dental providers' },
  { value: 'poor-experience', label: 'Poor experience' },
  { value: 'scheduling', label: 'Scheduling difficulties' },
  { value: 'treatment-cost', label: 'Treatment cost concerns' },
  { value: 'benefits-unclear', label: 'Membership benefits unclear' },
  { value: 'low-utilization', label: 'Low utilization' },
  { value: 'other', label: 'Other' },
]
